import React, { useState, useEffect, useMemo } from "react";
import {
  Download,
  Search,
  TrendingUp,
  Award,
  Users,
  Target,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import PeriodSelector from "../components/PeriodSelector.jsx";
import { exportCommissionReportToExcel } from "../components/ExcelExport.jsx";
import SummaryCard from "../components/SummaryCard.jsx";
import PerformanceIndicator from "../components/PerformanceIndicator.jsx";
import QuotaBadge from "../components/QuotaBadge.jsx";

const formatNumber = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCurrency = (num) => `$${formatNumber(num)}`;

const fetchReport = async ({ year, month, quarter, ytd }) => {
  const params = new URLSearchParams({ year });
  if (month !== null && month !== undefined) params.append("month", month);
  if (quarter) params.append("q", quarter);
  if (ytd) params.append("ytd", "true");

  const res = await fetch(
    `http://10.5.21.42:5000/api/commissions?${params.toString()}`,
  );
  if (!res.ok) throw new Error("Failed to fetch commission report");
  const json = await res.json();
  return json.data;
};

const roles = ["Manager", "Assistant Manager", "Employee"];

const CommissionReport = () => {
  const now = new Date();
  const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1);

  const [year, setYear] = useState(previousMonthDate.getFullYear());
  const [period, setPeriod] = useState(
    `month-${previousMonthDate.getMonth() + 1}`,
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeRole, setActiveRole] = useState("Manager");
  const [commissionAdjustments, setCommissionAdjustments] = useState({});
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    setLoading(true);

    let ytd = false;
    let m = undefined;
    let q = undefined;

    if (period === "ytd") ytd = true;
    else if (period.startsWith("month-")) m = Number(period.split("-")[1]);
    else if (period.startsWith("quarter-")) q = period.split("-")[1];

    fetchReport({ year, month: m, quarter: q, ytd })
      .then((data) => setReport(data))
      .catch((err) => {
        console.error(err);
        setReport(null);
      })
      .finally(() => setLoading(false));
  }, [year, period]);

  const stores = useMemo(() => {
    if (!report || !Array.isArray(report.locations)) return [];
    return report.locations.map((store) => ({
      storeId: store.storeId,
      storeName: store.storeName,
    }));
  }, [report]);

  const filteredSalesmen = useMemo(() => {
    if (!report) return [];
    if (!Array.isArray(report.locations)) return [];

    const salesmenList = [];

    report.locations.forEach((store) => {
      const salesmen = [
        ...(store.managers || []),
        ...(store.assistantManager ? [store.assistantManager] : []),
        ...(store.employees || []),
      ];

      if (!salesmen.length) {
        // No salesmen at all for this store, but still add a placeholder row for store
        salesmenList.push({
          storeId: store.storeId,
          hasCoManager: store.hasCoManager || false,
          storeName: store.storeName,
          storeCurrentSale: store.storeCurrentSale,
          storeLastSales: store.storeLastSales,
          quota: store.quota,
          quotaAchievedPercent: store.quotaAchievedPercent,
          storeUpDownPercent: store.storeUpDownPercent,
          storeCreditRate: 0,
          storeCreditAmt: 0,
          salesmanCurrentSale: 0,
          salesmanLastSales: 0,
          salesmanCommissionRate: 0,
          salesmanCommissionAmt: 0,
          name: "-", // Placeholder name
          employeeId: "",
          role: "-", // No role since no employee
          commissionTier: "-",
        });
        return; // Continue to next store
      }

      // Filter salesmen according to activeRole and store filters
      const filtered = salesmen.filter((s) => {
        if (
          activeRole === "All" ||
          s.role === activeRole ||
          (activeRole === "Manager" &&
            (s.role === "Manager" || s.role === "Manager2"))
        ) {
          if (
            activeRole === "Employee" &&
            selectedStoreId !== "all" &&
            String(store.storeId) !== String(selectedStoreId)
          ) {
            return false;
          }
          return true;
        }
        return false;
      });

      if (filtered.length === 0) {
        // No matching salesmen for this filter, still add a store-only row
        salesmenList.push({
          storeId: store.storeId,
          hasCoManager: store.hasCoManager || false,
          storeName: store.storeName,
          storeCurrentSale: store.storeCurrentSale,
          storeLastSales: store.storeLastSales,
          quota: store.quota,
          quotaAchievedPercent: store.quotaAchievedPercent,
          storeUpDownPercent: store.storeUpDownPercent,
          storeCreditRate: 0,
          storeCreditAmt: 0,
          salesmanCurrentSale: 0,
          salesmanLastSales: 0,
          salesmanCommissionRate: 0,
          salesmanCommissionAmt: 0,
          name: "-", // Placeholder name
          employeeId: "",
          role: "-", // No role since no employee matches
          commissionTier: "-",
        });
      } else {
        filtered.forEach((s) => {
          salesmenList.push({
            storeId: store.storeId,
            hasCoManager: store.hasCoManager || false,
            storeName: store.storeName,
            storeCurrentSale: store.storeCurrentSale,
            storeLastSales: store.storeLastSales,
            quota: store.quota,
            quotaAchievedPercent: store.quotaAchievedPercent,
            storeUpDownPercent: store.storeUpDownPercent,
            storeCreditRate: s.storeCreditRate,
            storeCreditAmt: s.storeCreditAmt,
            salesmanCurrentSale: s.salesmanCurrentSale,
            salesmanLastSales: s.salesmanLastSales,
            salesmanCommissionRate: s.salesmanCommissionRate,
            salesmanCommissionAmt: s.salesmanCommissionAmt,
            name: s.name,
            employeeId: s.employeeId || "",
            role: s.role,
            commissionTier: s.commissionTier || "-",
          });
        });
      }
    });

    return salesmenList;
  }, [report, activeRole, selectedStoreId]);

  const searchedSalesmen = useMemo(() => {
    if (!searchTerm) return filteredSalesmen;

    const term = searchTerm.toLowerCase();
    return filteredSalesmen.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        s.storeName.toLowerCase().includes(term) ||
        s.employeeId.toLowerCase().includes(term) ||
        String(s.storeId).includes(term),
    );
  }, [filteredSalesmen, searchTerm]);

  const sortedSalesmen = useMemo(() => {
    if (!sortConfig.key) return searchedSalesmen;

    const sorted = [...searchedSalesmen].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === "number" || typeof bVal === "number") {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [searchedSalesmen, sortConfig]);

  useEffect(() => {
    if (activeRole !== "Employee") setSelectedStoreId("all");
  }, [activeRole]);

  const storeTotals = useMemo(() => {
    if (!sortedSalesmen.length)
      return { current: 0, last: 0, quota: 0, quotaAchievedPercent: 0 };
    const storeIdsSeen = new Set();
    let currentSum = 0;
    let lastSum = 0;
    let quotaSum = 0;
    let quotaPercentSum = 0;
    let quotaCount = 0;

    sortedSalesmen.forEach((s) => {
      if (!storeIdsSeen.has(s.storeId)) {
        currentSum += Number(s.storeCurrentSale) || 0;
        lastSum += Number(s.storeLastSales) || 0;
        quotaSum += Number(s.quota) || 0;
        if (typeof s.quotaAchievedPercent === "number") {
          quotaPercentSum += s.quotaAchievedPercent;
          quotaCount++;
        }
        storeIdsSeen.add(s.storeId);
      }
    });

    return {
      current: currentSum,
      last: lastSum,
      quota: quotaSum,
      quotaAchievedPercent: quotaCount > 0 ? quotaPercentSum / quotaCount : 0,
    };
  }, [sortedSalesmen]);

  const totalSalesmanCommission = useMemo(() => {
    return sortedSalesmen.reduce(
      (sum, s) => sum + (Number(s.salesmanCommissionAmt) || 0),
      0,
    );
  }, [sortedSalesmen]);

  const totalStoreCommission = useMemo(() => {
    const storeIdsSeen = new Set();
    let sum = 0;
    sortedSalesmen.forEach((s) => {
      if (!storeIdsSeen.has(s.storeId)) {
        sum += Number(s.storeCreditAmt) || 0;
        storeIdsSeen.add(s.storeId);
      }
    });
    return sum;
  }, [sortedSalesmen]);

  const totalAdjustments = useMemo(() => {
    return Object.values(commissionAdjustments).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0,
    );
  }, [commissionAdjustments]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <ChevronUp className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-3 h-3 text-blue-600" />
    ) : (
      <ChevronDown className="w-3 h-3 text-blue-600" />
    );
  };

  const exportToExcel = () => {
    if (!sortedSalesmen.length) return;

    const exportData = sortedSalesmen.map((s) => {
      const salesmanCommission = Number(s.salesmanCommissionAmt) || 0;
      const storeCommission = Number(s.storeCreditAmt) || 0;
      const adjustment = 0;

      return {
        storeId: s.storeId,
        storeName: s.storeName,
        employeeId: s.employeeId,
        name: s.name,
        role: s.role,
        commissionTier: s.commissionTier || "-",
        storeCurrentSale: Number(s.storeCurrentSale) || 0,
        storeLastSales: Number(s.storeLastSales) || 0,
        quota: Number(s.quota) || 0,
        quotaAchievedPercent: Number(s.quotaAchievedPercent) || 0,
        storeUpDownPercent: s.storeUpDownPercent,
        storeCreditRate: Number(s.storeCreditRate) || 0,
        storeCommission,
        salesmanCurrentSale: Number(s.salesmanCurrentSale) || 0,
        salesmanLastSales: Number(s.salesmanLastSales) || 0,
        salesmanCommissionRate: Number(s.salesmanCommissionRate) || 0,
        salesmanCommission,
        totalCommission: salesmanCommission + storeCommission + adjustment,
      };
    });

    exportCommissionReportToExcel(exportData);
  };

  let periodLabel = "";
  if (period === "ytd") periodLabel = `Year to Date ${year}`;
  else if (period.startsWith("month-")) {
    const m = Number(period.split("-")[1]);
    periodLabel = `${new Date(year, m - 1).toLocaleString("default", {
      month: "long",
    })} ${year}`;
  } else if (period.startsWith("quarter-")) {
    periodLabel = `${period.split("-")[1].toUpperCase()} ${year}`;
  }

  const totalCommissions = totalStoreCommission + totalSalesmanCommission;
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto p-6 space-y-6">
        {!loading && report && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SummaryCard
              title="Total Commissions"
              value={formatCurrency(totalCommissions)}
              icon={Award}
              color="blue"
              subtitle="Store + Salesman"
            />
            <SummaryCard
              title="Total Sales"
              value={formatCurrency(storeTotals.current)}
              icon={TrendingUp}
              color="green"
              subtitle={`vs ${formatCurrency(storeTotals.last)} last period`}
            />
            <SummaryCard
              title="Active Salespeople"
              value={sortedSalesmen.length}
              icon={Users}
              color="purple"
              subtitle={`${activeRole}${activeRole === "All" ? "" : "s"}`}
            />
            <SummaryCard
              title="Avg Quota Achievement"
              value={`${storeTotals.quotaAchievedPercent.toFixed(1)}%`}
              icon={Target}
              color="orange"
              subtitle={`Target: ${formatCurrency(storeTotals.quota)}`}
            />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-6 items-center flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Year:
                </label>
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 w-28 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Month:
                </label>
                <PeriodSelector period={period} setPeriod={setPeriod} />
              </div>

              <div className="flex items-center gap-2 min-w-[280px] flex-1">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Search:
                </label>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by name, store, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!report || sortedSalesmen.length === 0}
            >
              <Download className="w-4 h-4" />
              Export to Excel
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            {roles.map((role) => (
              <button
                key={role}
                onClick={() => setActiveRole(role)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeRole === role
                    ? "bg-blue-600 text-white shadow-md scale-105"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105"
                }`}
              >
                {role}
              </button>
            ))}

            {activeRole === "Employee" && (
              <div className="ml-auto">
                <select
                  id="storeFilter"
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Stores</option>
                  {stores.map(({ storeId, storeName }) => (
                    <option key={storeId} value={storeId}>
                      {storeName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading report...</p>
          </div>
        )}

        {!loading && !report && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-12 text-center">
            <p className="text-red-600 font-medium text-lg">
              Failed to load report
            </p>
            <p className="text-gray-600 mt-2">
              Please try again or contact support
            </p>
          </div>
        )}

        {!loading && report && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-300 sticky top-0">
                  <tr>
                    <th
                      onClick={() => handleSort("storeName")}
                      className="group px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        Store
                        <SortIcon columnKey="storeName" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("name")}
                      className="group px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        Name
                        <SortIcon columnKey="name" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("role")}
                      className="group px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Role
                    </th>

                    <th
                      onClick={() => handleSort("storeCurrentSale")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Store Current Sale
                    </th>
                    {/* <th
                      onClick={() => handleSort("storeLastSales")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Store Last Sales
                    </th> */}
                    <th
                      onClick={() => handleSort("quota")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Quota
                    </th>
                    <th
                      onClick={() => handleSort("quotaAchievedPercent")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Quota Achieved %
                    </th>
                    <th
                      onClick={() => handleSort("storeCreditRate")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Store Credit Rate
                    </th>
                    <th
                      onClick={() => handleSort("storeCreditAmt")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Store Credit Amt
                    </th>
                    <th
                      onClick={() => handleSort("salesmanCurrentSale")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Salesman Current Sale
                    </th>
                    {/* <th
                      onClick={() => handleSort("salesmanLastSales")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Salesman Last Sales
                    </th> */}
                    <th
                      onClick={() => handleSort("salesmanCommissionRate")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Salesman Commission Rate
                    </th>
                    <th
                      onClick={() => handleSort("salesmanCommissionAmt")}
                      className="group px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      Salesman Commission Amt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedSalesmen.map((s) => (
                    <tr key={`${s.storeId}-${s.employeeId}`}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {s.storeId} -{s.storeName}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{s.name}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{s.role}</td>

                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.storeCurrentSale)}
                      </td>

                      {/* <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.storeLastSales)}
                      </td> */}
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.quota)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {QuotaBadge({ percent: s.quotaAchievedPercent })}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {s.storeCreditRate ? s.storeCreditRate.toFixed(2) : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.storeCreditAmt)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.salesmanCurrentSale)}
                      </td>
                      {/* <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.salesmanLastSales)}
                      </td> */}
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {s.salesmanCommissionRate
                          ? s.salesmanCommissionRate.toFixed(2)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {formatCurrency(s.salesmanCommissionAmt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionReport;
